import { trackFileSend } from '../../containers/AttachFileButtonContainer/tracking';
import { currentChatOpponentIdSelector } from '../../selectors';
import { EExceptionDomain } from '../../types/exceptionDomains';
import { getGenericLikeError } from '../../utils/logging/getGenericLikeError';

import { ERequestApiStatus, EWsClientEventType } from '../../types/appState';
import { TFileTypes } from '../../types/messageFiles';
import { TThunkAction } from '../../types/redux/thunk';
import { CancellationToken } from '../../utils/cancelationToken';
import { createOutcomingMessage } from '../../utils/messageUtils';
import { uploadFilesFailed, uploadFilesPending, uploadFilesSucceed } from '../files/actions';
import { addFiles, EAddFilesResult } from '../files/addFiles';
import { setDeleteMessage, setSendMessageFailed, setSendMessagePending } from '../sendMessage';
import { sendWsMessage } from '../wsMessage';
import { IMessageEvent } from '../wsMessage/types';

type TSendFileMessage = {
  file: File;
  chatId: string;
  type: TFileTypes;
  messageId?: string;
  failCallback?: () => void;
};

// В начальной реализации обрабатываем отправку одного фото-файла в одном сообщении без текста
export const sendFileMessage =
  ({ file, chatId, type, messageId, failCallback }: TSendFileMessage): TThunkAction<Promise<void>> =>
  async (dispatch, getState, { httpApi, logger, custom: { messagesMap, socket } }) => {
    if (!socket) {
      return;
    }

    const { clientEvents } = socket;

    const state = getState();

    const {
      user: { userId },
    } = state;
    const opponentId = currentChatOpponentIdSelector(state);

    const cancellationToken = new CancellationToken();

    const message = createOutcomingMessage({ uid: messageId, chatId, userId, type: 'textAndFiles' });

    // Подставляем в state blob File, чтобы сразу отобразить изображение через FileReader
    dispatch(
      uploadFilesPending({
        uid: message.messageId,
        blobFiles: [{ file, cancellationToken, type }],
        chatId,
      }),
    );

    const messageEvent: IMessageEvent = {
      chatId,
      status: ERequestApiStatus.Pending,
      type: EWsClientEventType.Message,
      timestamp: Date.now(),
    };

    dispatch(setSendMessagePending({ message, messageId: message.messageId, messagesMap }));

    try {
      // Шаг 1. Отправляем файлы в мск files
      const { response, responseStatusCode } = await addFiles({ httpApi, logger }, { file, cancellationToken });

      if (responseStatusCode !== 200 || !response.addFilesResult || !response.addFilesResult.length) {
        clientEvents.set(message.messageId, { ...messageEvent, status: ERequestApiStatus.Failed });
        dispatch(setSendMessageFailed({ message, messageId: message.messageId, messagesMap }));
        dispatch(uploadFilesFailed({ uid: message.messageId, chatId }));
        trackFileSend({ fileType: type, chatId, status: 'failure', opponentId });

        return;
      }

      const resultFile = response.addFilesResult[0];

      if (
        resultFile.result === EAddFilesResult.Fail ||
        resultFile.result === EAddFilesResult.NoContent ||
        resultFile.result === EAddFilesResult.InvalidContent
      ) {
        dispatch(setDeleteMessage({ chatId: message.chatId, messageId: message.messageId, messagesMap }));
        trackFileSend({ fileType: type, chatId, status: 'failure', opponentId });

        if (failCallback) {
          failCallback();
        }

        return;
      }

      if (resultFile.result === EAddFilesResult.Success && resultFile.id && resultFile.fileName) {
        const uploadedFile = {
          uid: resultFile.id,
          name: resultFile.fileName,
          asDocument: type !== 'image',
        };

        dispatch(
          uploadFilesSucceed({
            uid: message.messageId,
            uploadedFiles: [uploadedFile],
            chatId,
          }),
        );

        // Шаг 2. Отправляем загруженные файлы по сокету как сообщение
        await dispatch(sendWsMessage({ message, files: [uploadedFile] }));
      }
    } catch (error) {
      /* istanbul ignore next*/
      if (cancellationToken.isCanceled()) {
        return;
      }

      const exception = getGenericLikeError({
        error,
        domain: EExceptionDomain.Messages,
        message: 'Не удалось загрузить файл',
      });

      logger.warning(exception);

      clientEvents.set(message.messageId, { ...messageEvent, status: ERequestApiStatus.Failed });
      dispatch(setSendMessageFailed({ message, messageId: message.messageId, messagesMap }));
      dispatch(uploadFilesFailed({ uid: message.messageId, chatId }));
      trackFileSend({ fileType: type, chatId, status: 'failure', opponentId });
    }
  };
